import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../axiosConfig'; // Используем настроенный axiosInstance
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';
import { ru } from 'date-fns/locale'; // Подключаем русский язык для календаря

function OrderHistory() {
    const [orders, setOrders] = useState([]);
    const [expandedOrders, setExpandedOrders] = useState({});
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [repeatingOrderId, setRepeatingOrderId] = useState(null);
    const getCurrentMonthRange = () => [
        moment().startOf('month').toDate(),
        moment().endOf('day').toDate(),
    ];
    const [dateRange, setDateRange] = useState(getCurrentMonthRange);
    const [startDate, endDate] = dateRange;

    const showToast = (text, danger = false) => {
        if (!window.Toastify) return;
        window.Toastify({
            text,
            duration: 4500,
            backgroundColor: danger
                ? "linear-gradient(to right, #ff5f6d, #ffc371)"
                : "linear-gradient(to right, #00b09b, #96c93d)",
        }).showToast();
    };

    const fetchOrders = useCallback(() => {
        setLoading(true);
        const apiUrl = `/api/order-history/`;
        const params = {
            start_date: startDate ? moment(startDate).format('YYYY-MM-DD') : '',
            end_date: endDate ? moment(endDate).format('YYYY-MM-DD') : '',
        };

        axiosInstance.get(apiUrl, { params })
            .then(response => {
                setOrders(response.data.orders);
                setLoading(false);
            })
            .catch(error => {
                console.error('Ошибка при получении истории заказов:', error);
                setLoading(false);
            });
    }, [startDate, endDate]);

    useEffect(() => {
        // Загружаем историю сразу и обновляем при изменении фильтра дат
        fetchOrders();
    }, [fetchOrders]);

    const handleExportToExcel = () => {
        setDownloading(true);
        const apiUrl = `/api/export_order_history_to_excel/`;
        const params = {
            start_date: startDate ? moment(startDate).format('YYYY-MM-DD') : '',
            end_date: endDate ? moment(endDate).format('YYYY-MM-DD') : '',
        };

        axiosInstance.get(apiUrl, { params, responseType: 'blob' })
            .then((response) => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `order_history_${params.start_date}_to_${params.end_date}.xlsx`);
                document.body.appendChild(link);
                link.click();
                setDownloading(false);
            })
            .catch((error) => {
                console.error('Ошибка при выгрузке в Excel:', error);
                setDownloading(false);
            });
    };

    const toggleOrderDetails = (orderId) => {
        setExpandedOrders(prevExpandedOrders => ({
            ...prevExpandedOrders,
            [orderId]: !prevExpandedOrders[orderId] // Инвертируем состояние для раскрытия/скрытия
        }));
    };

    const handleRepeatOrder = async (orderId, event) => {
        event.stopPropagation();
        if (repeatingOrderId) return;

        setRepeatingOrderId(orderId);
        try {
            const response = await axiosInstance.post(`/api/order-history/${orderId}/repeat/`);
            if (response.data.success) {
                showToast(`Заказ #${orderId} повторен как новый #${response.data.order_id}`);
                fetchOrders();
            } else {
                showToast(response.data.error || 'Не удалось повторить заказ', true);
            }
        } catch (error) {
            const shortages = error?.response?.data?.shortages;
            if (Array.isArray(shortages) && shortages.length > 0) {
                const details = shortages
                    .slice(0, 3)
                    .map(item => `${item.name}: ${item.available}/${item.requested}`)
                    .join('; ');
                showToast(`Недостаточно остатков: ${details}`, true);
            } else {
                const message = error?.response?.data?.error
                    || error?.response?.data?.message
                    || 'Ошибка при повторении заказа';
                showToast(message, true);
            }
        } finally {
            setRepeatingOrderId(null);
        }
    };

    // Функция для форматирования даты
    const formatDate = (isoDate) => {
        return moment(isoDate).format('DD.MM.YYYY [Время] HHч mmм ssс');
    };

    return (
        <div className="order-history container mt-4">
            <h2>История заказов</h2>
            <div className="d-flex align-items-center mb-4">
                <div className="me-3">
                    <DatePicker
                        selectsRange
                        startDate={startDate}
                        endDate={endDate}
                        onChange={(update) => {
                            if (!update || (!update[0] && !update[1])) {
                                setDateRange(getCurrentMonthRange());
                                return;
                            }
                            setDateRange(update);
                        }}
                        isClearable={false}
                        dateFormat="dd MMM yyyy"
                        placeholderText="Выберите период"
                        className="form-control"
                        wrapperClassName="warehouse-datepicker-wrapper"
                        calendarClassName="warehouse-datepicker-calendar"
                        popperClassName="warehouse-datepicker-popper"
                        popperPlacement="bottom-start"
                        popperProps={{ strategy: 'fixed' }}
                        locale={ru}
                    />
                </div>
                <button
                    className="btn btn-success ms-2"
                    onClick={handleExportToExcel}
                    disabled={downloading || orders.length === 0}
                >
                    {downloading ? (
                        <i className="fas fa-spinner fa-spin"></i>
                    ) : (
                        <i className="fas fa-file-excel"></i>
                    )}
                    Экспорт в Excel
                </button>
            </div>
            {loading && (
                <div className="text-center mb-3">
                    <div className="spinner-border" role="status">
                        <span className="visually-hidden">Загрузка...</span>
                    </div>
                </div>
            )}
            <table className="table table-striped">
                <thead>
                    <tr>
                        <th>Номер заказа</th>
                        <th>Дата создания</th>
                        <th>Количество товаров</th>
                        <th>Действия</th>
                    </tr>
                </thead>
                <tbody>
                    {!loading && orders.length === 0 ? (
                        <tr>
                            <td colSpan={4}>Заказы не найдены.</td>
                        </tr>
                    ) : (
                        !loading && orders.map(order => (
                            <React.Fragment key={order.order_id}>
                                <tr onClick={() => toggleOrderDetails(order.order_id)} style={{ cursor: 'pointer' }}>
                                    <td>Заказ #{order.order_id}</td>
                                    <td>{formatDate(order.created_at)}</td>
                                    <td>{order.items.length}</td>
                                    <td>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            onClick={(event) => handleRepeatOrder(order.order_id, event)}
                                            disabled={repeatingOrderId === order.order_id}
                                        >
                                            {repeatingOrderId === order.order_id ? 'Повторяем...' : 'Повторить заказ'}
                                        </button>
                                    </td>
                                </tr>
                                {expandedOrders[order.order_id] && (
                                    <tr>
                                        <td colSpan={4}>
                                            <table className="table">
                                                <thead>
                                                    <tr>
                                                        <th>Компонент</th>
                                                        <th>Количество</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {order.items.map(item => (
                                                        <tr key={item.component}>
                                                            <td>{item.component}</td>
                                                            <td>{item.quantity}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default OrderHistory;
